import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon } from "../../icons";
import { Edit as EditIcon } from "../../icons";
import { DeleteOutline as DeleteIcon } from "../../icons";
import { Phone as PhoneIcon } from "../../icons";
import { PhotoCamera as PhotoCameraIcon } from "../../icons";
import { OpenInNew as OpenInNewIcon } from "../../icons";
import { Navigation as NavigationIcon } from "../../icons";
import { ShareLocation as ShareLocationIcon } from "../../icons";
import FoodOrderMap from "./FoodOrderMap";
import { auth, uploadImage } from "../../firebase";
import PageHeader from "../PageHeader";
import { pageContentSx } from "../../pageLayout";
import { OrderSummary } from "./FoodOrderStatus";
import { geocodeAddress, reverseGeocodeAddress } from "../../utils/geo";
import LocationPicker from "../../components/LocationPicker";
import IOSPurchaseUnavailable from "../../components/IOSPurchaseUnavailable";
import { isIOSApp } from "../../utils/device";
import { useSubscriptionContext } from "../SubscriptionContext";
import {
  MEAL_TYPES,
  STORE_KINDS,
  categoriesFor,
  storeKind,
  NEXT_STATUS_ACTION,
  ORDER_STATUS,
  confirmFoodDelivery,
  directionsUrl,
  updateRiderLocation,
  deleteMenuItem,
  fetchOwnRestaurant,
  formatMoney,
  CURRENCIES,
  deviceTimeZone,
  guessCurrency,
  parsePrice,
  isRestaurantOpen,
  nextOrderStatus,
  saveMenuItem,
  saveRestaurant,
  setOrderStatus,
  subscribeMenu,
  subscribeOrdersAsRestaurant,
  timestampMs,
} from "./foodUtils";

const ACTIVE_STATUSES = ["pending", "accepted", "on_the_way", "ready"];

const EMPTY_RESTAURANT = {
  kind: "food",
  name: "",
  category: "pizza",
  description: "",
  logo: "",
  coverPhoto: "",
  address: "",
  city: "",
  openTime: "11:00",
  closeTime: "23:30",
  mealTypes: ["almuerzo", "cena"],
  delivery: true,
  deliveryFee: "",
  deliveryTime: "30-45 min",
  takeaway: true,
  minOrder: "",
  transferAlias: "",
  currency: "",
};

const EMPTY_ITEM = { name: "", description: "", price: "", section: "", photo: "", available: true };

const toNumber = parsePrice;

// Botón para subir una foto (logo, portada, plato) con uploadImage.
const PhotoField = ({ label, value, onChange, round }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadImage(file));
    } catch (error) {
      alert(error?.message || "No se pudo subir la foto.");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Avatar src={value} variant={round ? "circular" : "rounded"} sx={{ width: 64, height: 64, bgcolor: "action.hover", color: "text.secondary" }}>
        <PhotoCameraIcon />
      </Avatar>
      <Button component="label" variant="outlined" size="small" disabled={uploading}>
        {uploading ? <CircularProgress size={18} /> : value ? `Cambiar ${label}` : `Subir ${label}`}
        <input hidden type="file" accept="image/*" onChange={handleFile} />
      </Button>
      {value && !uploading && (
        <Button size="small" color="inherit" onClick={() => onChange("")}>Quitar</Button>
      )}
    </Box>
  );
};

const RestaurantForm = ({ restaurant, onSaved }) => {
  const [form, setForm] = useState(() => ({
    ...EMPTY_RESTAURANT,
    ...(restaurant || {}),
    kind: restaurant ? storeKind(restaurant) : "food",
    currency: restaurant?.currency || (restaurant ? "ARS" : guessCurrency()),
  }));
  const [locating, setLocating] = useState(false);
  const hasPin = typeof form.latitude === "number" && typeof form.longitude === "number";

  const pickLocation = async (latitude, longitude) => {
    setForm((prev) => ({ ...prev, latitude, longitude }));
    if (form.address.trim()) return;
    const found = await reverseGeocodeAddress(latitude, longitude);
    if (found) setForm((prev) => (prev.address.trim() ? prev : { ...prev, address: found }));
  };

  const searchAddress = async () => {
    const query = [form.address, form.city].map((v) => v.trim()).filter(Boolean).join(", ");
    if (!query) return setError("Escribí la dirección para buscarla en el mapa.");
    setLocating(true);
    const found = await geocodeAddress(query);
    setLocating(false);
    if (found) setForm((prev) => ({ ...prev, latitude: found.latitude, longitude: found.longitude }));
    else setError("No encontramos esa dirección. Marcala tocando el mapa.");
  };
  const isMarket = form.kind === "market";
  const setKind = (kind) =>
    setForm((prev) => (prev.kind === kind ? prev : { ...prev, kind, category: categoriesFor(kind)[0].id }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setFromEvent = (key) => (event) => set(key)(event.target.value);

  const toggleMeal = (id) =>
    setForm((prev) => ({
      ...prev,
      mealTypes: prev.mealTypes.includes(id) ? prev.mealTypes.filter((m) => m !== id) : [...prev.mealTypes, id],
    }));

  const submit = async () => {
    setError("");
    if (!form.name.trim()) return setError("Poné el nombre del local.");
    if (!form.address.trim()) return setError("Poné la dirección del local.");
    if (!form.delivery && !form.takeaway) return setError("Elegí envío, retiro o los dos.");
    setSaving(true);
    try {
      const data = {
        uid: auth.currentUser.uid,
        kind: form.kind,
        name: form.name.trim().slice(0, 80),
        category: form.category,
        description: form.description.trim().slice(0, 600),
        logo: form.logo,
        coverPhoto: form.coverPhoto,
        address: form.address.trim().slice(0, 200),
        city: form.city.trim().slice(0, 80),
        openTime: form.openTime || "",
        closeTime: form.closeTime || "",
        mealTypes: isMarket ? [] : form.mealTypes,
        delivery: !!form.delivery,
        deliveryFee: form.delivery ? toNumber(form.deliveryFee) : 0,
        deliveryTime: form.deliveryTime.trim().slice(0, 40),
        takeaway: !!form.takeaway,
        minOrder: toNumber(form.minOrder),
        transferAlias: form.transferAlias.trim().slice(0, 80),
        currency: form.currency,
        timeZone: deviceTimeZone() || restaurant?.timeZone || "",
        active: true,
        acceptingOrders: restaurant ? restaurant.acceptingOrders !== false : true,
      };
      // Coordenadas del local para el mapa del recorrido del pedido: el pin
      // del mapa, o si no lo marcó, la dirección geocodificada.
      if (hasPin) {
        data.latitude = form.latitude;
        data.longitude = form.longitude;
      } else {
        const found = await geocodeAddress([data.address, data.city].filter(Boolean).join(", "));
        if (found) {
          data.latitude = found.latitude;
          data.longitude = found.longitude;
        }
      }
      const id = await saveRestaurant(restaurant?.id, data);
      onSaved({ ...restaurant, ...data, id });
    } catch (err) {
      console.error("Error guardando el local:", err);
      setError("No se pudo guardar. Probá de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 1 }}>
        {STORE_KINDS.map((k) => (
          <Button
            key={k.id}
            fullWidth
            variant={form.kind === k.id ? "contained" : "outlined"}
            onClick={() => setKind(k.id)}
          >
            {k.emoji} {k.id === "market" ? "Súper o almacén" : "Restaurante"}
          </Button>
        ))}
      </Box>
      <TextField label="Nombre del local" value={form.name} onChange={setFromEvent("name")} required />
      <TextField select label={isMarket ? "Rubro" : "Tipo de comida"} value={form.category} onChange={setFromEvent("category")}>
        {categoriesFor(form.kind).map((c) => (
          <MenuItem key={c.id} value={c.id}>{c.emoji} {c.label}</MenuItem>
        ))}
      </TextField>
      <TextField label="Descripción" value={form.description} onChange={setFromEvent("description")} multiline minRows={2} />
      <PhotoField label="logo" value={form.logo} onChange={set("logo")} round />
      <PhotoField label="portada" value={form.coverPhoto} onChange={set("coverPhoto")} />
      <TextField label="Dirección del local" value={form.address} onChange={setFromEvent("address")} required />
      <TextField label="Ciudad y país" placeholder="Ej.: Córdoba, Argentina" value={form.city} onChange={setFromEvent("city")} />
      <LocationPicker
        latitude={form.latitude}
        longitude={form.longitude}
        onPick={pickLocation}
        onSearchAddress={searchAddress}
        busy={locating}
      />
      <TextField select label="Moneda" value={form.currency} onChange={setFromEvent("currency")}>
        {CURRENCIES.map((code) => (
          <MenuItem key={code} value={code}>{code} ({formatMoney(1500, code)})</MenuItem>
        ))}
      </TextField>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField label="Abre" type="time" value={form.openTime} onChange={setFromEvent("openTime")} InputLabelProps={{ shrink: true }} fullWidth />
        <TextField label="Cierra" type="time" value={form.closeTime} onChange={setFromEvent("closeTime")} InputLabelProps={{ shrink: true }} fullWidth />
      </Box>

      {!isMarket && (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>¿Para qué comidas?</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {MEAL_TYPES.map((m) => {
            const selected = form.mealTypes.includes(m.id);
            return (
              <Chip key={m.id} label={m.label} onClick={() => toggleMeal(m.id)} color={selected ? "primary" : "default"} variant={selected ? "filled" : "outlined"} />
            );
          })}
        </Box>
      </Box>
      )}

      <FormControlLabel control={<Switch checked={!!form.delivery} onChange={(e) => set("delivery")(e.target.checked)} />} label="Hago envíos" />
      {form.delivery && (
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Costo de envío"
            value={form.deliveryFee}
            onChange={setFromEvent("deliveryFee")}
            inputMode="numeric"
            helperText="0 o vacío = gratis"
            InputProps={{ startAdornment: <InputAdornment position="start">{form.currency}</InputAdornment> }}
            fullWidth
          />
          <TextField label="Demora" value={form.deliveryTime} onChange={setFromEvent("deliveryTime")} placeholder="30-45 min" fullWidth />
        </Box>
      )}
      <FormControlLabel control={<Switch checked={!!form.takeaway} onChange={(e) => set("takeaway")(e.target.checked)} />} label="Se puede retirar en el local" />
      <TextField
        label="Pedido mínimo"
        value={form.minOrder}
        onChange={setFromEvent("minOrder")}
        inputMode="numeric"
        InputProps={{ startAdornment: <InputAdornment position="start">{form.currency}</InputAdornment> }}
      />
      <TextField
        label="Datos para transferencias (opcional)"
        placeholder="Alias, CBU, CLABE, IBAN, Pix, cuenta…"
        value={form.transferAlias}
        onChange={setFromEvent("transferAlias")}
        helperText="Si lo completás, el cliente puede elegir pagar por transferencia o billetera virtual. Siempre se puede pagar en efectivo."
      />

      {error && <Alert severity="error">{error}</Alert>}
      <Button variant="contained" size="large" onClick={submit} disabled={saving}>
        {saving ? <CircularProgress size={24} color="inherit" /> : restaurant ? "Guardar cambios" : "Crear mi local"}
      </Button>
    </Box>
  );
};

const MenuEditor = ({ restaurantId, isMarket, currency }) => {
  const noun = isMarket ? "producto" : "plato";
  const [menu, setMenu] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => subscribeMenu(restaurantId, setMenu, () => setMenu([])), [restaurantId]);

  const sectionNames = useMemo(
    () => [...new Set((menu || []).map((m) => m.section?.trim()).filter(Boolean))],
    [menu]
  );

  const save = async () => {
    if (!editing.name.trim() || !toNumber(editing.price)) {
      alert("Completá el nombre y el precio.");
      return;
    }
    setSaving(true);
    try {
      await saveMenuItem(restaurantId, editing.id, {
        name: editing.name.trim().slice(0, 120),
        description: editing.description.trim().slice(0, 400),
        price: toNumber(editing.price),
        section: (editing.section || "").trim().slice(0, 60),
        photo: editing.photo || "",
        available: editing.available !== false,
      });
      setEditing(null);
    } catch (error) {
      console.error("Error guardando el plato:", error);
      alert(`No se pudo guardar el ${noun}.`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Borrar "${item.name}" del menú?`)) return;
    await deleteMenuItem(restaurantId, item.id).catch((error) => console.error("Error borrando:", error));
  };

  const toggleAvailable = (item) =>
    saveMenuItem(restaurantId, item.id, { available: item.available === false }).catch((error) => console.error(error));

  return (
    <Box>
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ ...EMPTY_ITEM })} sx={{ mb: 2 }}>
        Agregar {noun}
      </Button>
      {menu === null && <CircularProgress />}
      {menu && !menu.length && (
        <Typography color="text.secondary">Cargá tus {noun}s para empezar a recibir pedidos.</Typography>
      )}
      {menu?.map((item) => (
        <Paper key={item.id} variant="outlined" sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.25, mb: 1, borderRadius: 2 }}>
          <Avatar src={item.photo} variant="rounded" sx={{ width: 52, height: 52, bgcolor: "action.hover" }}>🍽️</Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>{item.name}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {formatMoney(item.price, currency)}{item.section ? ` · ${item.section}` : ""}
            </Typography>
          </Box>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={<Switch size="small" checked={item.available !== false} onChange={() => toggleAvailable(item)} />}
            label={<Typography variant="caption">{item.available === false ? "Sin stock" : "Disponible"}</Typography>}
            labelPlacement="bottom"
          />
          <IconButton onClick={() => setEditing({ ...EMPTY_ITEM, ...item, price: String(item.price ?? "") })} aria-label="Editar">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => remove(item)} aria-label="Borrar">
            <DeleteIcon />
          </IconButton>
        </Paper>
      ))}

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editing?.id ? `Editar ${noun}` : `Nuevo ${noun}`}</DialogTitle>
        {editing && (
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
            <TextField label="Nombre" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required />
            <TextField label="Descripción" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} multiline minRows={2} />
            <TextField
              label="Precio"
              value={editing.price}
              onChange={(e) => setEditing({ ...editing, price: e.target.value })}
              inputMode="numeric"
              required
              InputProps={{ startAdornment: <InputAdornment position="start">{currency || "ARS"}</InputAdornment> }}
            />
            <Autocomplete
              freeSolo
              options={sectionNames}
              value={editing.section}
              onInputChange={(_, value) => setEditing((prev) => ({ ...prev, section: value }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isMarket ? "Góndola" : "Sección del menú"}
                  placeholder={isMarket ? "Ej.: Almacén, Lácteos, Limpieza" : "Ej.: Pizzas, Bebidas, Postres"}
                />
              )}
            />
            <PhotoField label="foto" value={editing.photo} onChange={(photo) => setEditing((prev) => ({ ...prev, photo }))} />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Pide el código que el cliente ve en su app y cierra el pedido.
const DeliveryCodeDialog = ({ order, onClose }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await confirmFoodDelivery(order.id, code);
      if (result.ok) onClose();
      else setError(`Código incorrecto. Te quedan ${result.attemptsLeft} intentos.`);
    } catch (err) {
      setError(err?.message || "No se pudo confirmar la entrega.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Confirmar entrega</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pedile a {order.buyerName} el código de 4 números que le aparece en la app y en la notificación.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={(e) => e.key === "Enter" && code.length === 4 && submit()}
          inputProps={{ inputMode: "numeric", style: { fontSize: 32, textAlign: "center", letterSpacing: 12 } }}
          placeholder="0000"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={busy || code.length !== 4}>Confirmar</Button>
      </DialogActions>
    </Dialog>
  );
};

// Mientras está prendido, manda la ubicación del celular del repartidor al
// pedido (cada ~10 s) para que el cliente la vea en el mapa.
const useShareRiderLocation = (orderId) => {
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState("");
  const lastSent = useRef(0);

  useEffect(() => {
    if (!sharing || !orderId) return undefined;
    if (!navigator.geolocation) {
      setError("Este dispositivo no permite compartir la ubicación.");
      setSharing(false);
      return undefined;
    }
    const watchId = navigator.geolocation.watchPosition(
      ({ coords }) => {
        if (Date.now() - lastSent.current < 10000) return;
        lastSent.current = Date.now();
        updateRiderLocation(orderId, coords.latitude, coords.longitude).catch((err) => console.error(err));
      },
      () => {
        setError("No se pudo obtener la ubicación. Revisá los permisos.");
        setSharing(false);
      },
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [sharing, orderId]);

  return { sharing, setSharing, error };
};

const RiderTools = ({ order }) => {
  const { sharing, setSharing, error } = useShareRiderLocation(order.status === "on_the_way" ? order.id : null);
  const destination = order.deliveryLocation;
  return (
    <Box sx={{ mt: 1.5 }}>
      {(destination || order.restaurantLocation) && <FoodOrderMap order={order} height={200} />}
      <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<NavigationIcon />}
          href={destination ? directionsUrl(destination) : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(order.address)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Cómo llegar
        </Button>
        {order.status === "on_the_way" && (
          <Button
            size="small"
            variant={sharing ? "contained" : "outlined"}
            color={sharing ? "success" : "primary"}
            startIcon={<ShareLocationIcon />}
            onClick={() => setSharing(!sharing)}
          >
            {sharing ? "Compartiendo ubicación" : "Compartir mi ubicación"}
          </Button>
        )}
      </Box>
      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      {sharing && (
        <Typography variant="caption" color="text.secondary">
          Dejá esta pantalla abierta mientras hacés el envío.
        </Typography>
      )}
    </Box>
  );
};

const IncomingOrders = ({ uid }) => {
  const [orders, setOrders] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => subscribeOrdersAsRestaurant(uid, setOrders, () => setOrders([])), [uid]);

  const changeStatus = async (order, status) => {
    if (status === "delivered") {
      setConfirming(order);
      return;
    }
    if (status === "rejected" && !window.confirm("¿Rechazar este pedido?")) return;
    setBusyId(order.id);
    try {
      await setOrderStatus(order.id, status);
    } catch (error) {
      console.error("Error cambiando el estado:", error);
      alert("No se pudo actualizar el pedido.");
    } finally {
      setBusyId(null);
    }
  };

  if (orders === null) return <CircularProgress />;
  const list = orders.filter((o) => ACTIVE_STATUSES.includes(o.status) !== showPast);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Chip label="En curso" color={!showPast ? "primary" : "default"} onClick={() => setShowPast(false)} />
        <Chip label="Anteriores" color={showPast ? "primary" : "default"} onClick={() => setShowPast(true)} />
      </Box>
      {!list.length && (
        <Typography color="text.secondary">
          {showPast ? "Todavía no hay pedidos terminados." : "No hay pedidos en curso. Te avisamos con una notificación cuando entre uno."}
        </Typography>
      )}
      {list.map((order) => {
        const next = nextOrderStatus(order);
        const createdMs = timestampMs(order.createdAt);
        return (
          <Paper key={order.id} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 3, borderColor: order.status === "pending" ? "warning.main" : "divider", borderWidth: order.status === "pending" ? 2 : 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }} noWrap>{order.buyerName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {createdMs ? new Date(createdMs).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : ""}
                  {" · "}
                  {order.mode === "delivery" ? "Envío" : "Retiro"}
                </Typography>
              </Box>
              <Chip size="small" label={ORDER_STATUS[order.status]?.label} color={ORDER_STATUS[order.status]?.color || "default"} />
            </Box>
            <OrderSummary order={order} />
            {order.phone && (
              <Button size="small" startIcon={<PhoneIcon />} href={`tel:${order.phone.replace(/[^\d+]/g, "")}`} sx={{ mt: 1 }}>
                {order.phone}
              </Button>
            )}
            {order.mode === "delivery" && ["accepted", "on_the_way"].includes(order.status) && <RiderTools order={order} />}
            {ACTIVE_STATUSES.includes(order.status) && (
              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                {next && (
                  <Button variant="contained" onClick={() => changeStatus(order, next)} disabled={busyId === order.id} sx={{ flexGrow: 1 }}>
                    {NEXT_STATUS_ACTION[next]}
                  </Button>
                )}
                {order.status === "pending" && (
                  <Button color="error" onClick={() => changeStatus(order, "rejected")} disabled={busyId === order.id}>
                    Rechazar
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        );
      })}
      {confirming && <DeliveryCodeDialog order={confirming} onClose={() => setConfirming(null)} />}
    </Box>
  );
};

const RestaurantPanel = () => {
  const uid = auth.currentUser?.uid;
  const [restaurant, setRestaurant] = useState(undefined);
  const [tab, setTab] = useState("pedidos");
  const [pendingCount, setPendingCount] = useState(0);
  const { subscription, isSubscriptionActive, loading: subscriptionLoading } = useSubscriptionContext();

  useEffect(() => {
    if (!uid) return;
    fetchOwnRestaurant(uid).then(setRestaurant).catch(() => setRestaurant(null));
  }, [uid]);

  useEffect(() => {
    if (!uid || !restaurant) return undefined;
    return subscribeOrdersAsRestaurant(uid, (orders) => setPendingCount(orders.filter((o) => o.status === "pending").length));
  }, [uid, restaurant]);

  const toggleAccepting = async (checked) => {
    setRestaurant((prev) => ({ ...prev, acceptingOrders: checked }));
    await saveRestaurant(restaurant.id, { acceptingOrders: checked }).catch((error) => {
      console.error(error);
      setRestaurant((prev) => ({ ...prev, acceptingOrders: !checked }));
    });
  };

  // Sin plan no se puede crear un local, ni con un plan cancelado al que le
  // queda período (firestore.rules pide status ACTIVE). Quien ya tiene uno
  // conserva el panel para terminar los pedidos que tenía en curso.
  if (restaurant === null && !subscriptionLoading && (!isSubscriptionActive || subscription?.status !== "ACTIVE")) {
    return isIOSApp() ? <IOSPurchaseUnavailable /> : <Navigate to="/subscription" replace />;
  }

  return (
    <>
      <PageHeader
        title={restaurant ? restaurant.name : "Sumá tu local"}
        subtitle={restaurant ? "Panel del local" : "Recibí pedidos en Notiline"}
        rightSlot={restaurant ? (
          <IconButton color="inherit" component={Link} to={`/pedidos/${restaurant.id}`} aria-label="Ver mi local">
            <OpenInNewIcon />
          </IconButton>
        ) : null}
      />
      <Box sx={{ ...pageContentSx, maxWidth: { xs: "100%", md: 720 }, py: 2, pb: 8 }}>
        {restaurant === undefined && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>}

        {restaurant === null && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Cargá tu restaurante, súper o almacén con sus productos: está incluido en todos los planes. Los clientes te piden desde Notiline, pagan en efectivo o por
              transferencia, y vos vas marcando el estado del pedido.
            </Alert>
            <RestaurantForm restaurant={null} onSaved={(saved) => { setRestaurant(saved); setTab("menu"); }} />
          </>
        )}

        {restaurant && (
          <>
            {!subscriptionLoading && !isSubscriptionActive && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={isIOSApp() ? null : <Button color="inherit" size="small" component={Link} to="/subscription">Ver planes</Button>}
              >
                Tu plan no está activo: tu local no aparece en Pedidos y no recibe pedidos nuevos. Podés terminar los que ya tenés en curso.
              </Alert>
            )}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {isRestaurantOpen(restaurant) ? "Abierto: tomando pedidos" : "Cerrado"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {restaurant.openTime && restaurant.closeTime ? `Horario ${restaurant.openTime} a ${restaurant.closeTime}. ` : ""}
                  Apagalo para pausar los pedidos.
                </Typography>
              </Box>
              <Switch checked={restaurant.acceptingOrders !== false} onChange={(e) => toggleAccepting(e.target.checked)} />
            </Paper>

            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth" sx={{ mb: 2 }}>
              <Tab value="pedidos" label={<Badge color="error" badgeContent={pendingCount}>Pedidos</Badge>} />
              <Tab value="menu" label={storeKind(restaurant) === "market" ? "Productos" : "Menú"} />
              <Tab value="local" label="Mi local" />
            </Tabs>
            {tab === "pedidos" && <IncomingOrders uid={uid} />}
            {tab === "menu" && <MenuEditor restaurantId={restaurant.id} isMarket={storeKind(restaurant) === "market"} currency={restaurant.currency} />}
            {tab === "local" && (
              <RestaurantForm
                key={restaurant.id}
                restaurant={restaurant}
                onSaved={(saved) => { setRestaurant(saved); setTab("pedidos"); }}
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default RestaurantPanel;
